import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import { useMenus } from "../../config/menus.tsx";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import { formatDate } from "../../config/formatters.ts";
import ChangePasswordDialog from "./changePasswordDialog.tsx";

interface UserItem {
  id: number;
  name: string;
  username: string;
  isSuperAdmin: boolean;
  status: boolean;
  last_loggedin: string;
}

export default function Users() {
  const navigate = useNavigate();
  const { user: currentUser } = useMenus();
  const [users, setUsers] = useState<UserItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getUsers();
  }, [page, limit, search]);

  const getUsers = async () => {
    setLoading(true);
    const response = await request.post("users", {
      search: search,
      limit,
      offset: page,
      orderColumn: "created_at",
      orderDirection: "desc",
    });
    setUsers(response.data.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    id: 0,
    name: "",
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "name",
      label: "Name",
      minWidth: 170,
      format: (value) => value,
    },
    {
      id: "username",
      label: "Username",
      minWidth: 150,
      format: (value) => value,
    },
    {
      id: "isSuperAdmin",
      label: "Role",
      minWidth: 130,
      format: (value) => (
        <Box
          component="span"
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: "999px",
            fontSize: "0.75rem",
            fontWeight: 600,
            bgcolor: value ? "#ede9fe" : "#dbeafe",
            color: value ? "#7c3aed" : "#1d4ed8",
          }}
        >
          {value ? "Super Admin" : "Staff"}
        </Box>
      ),
    },
    {
      id: "last_loggedin",
      label: "Last Login",
      minWidth: 160,
      format: (value) => (value ? formatDate(value) : "—"),
    },
    {
      id: "status",
      label: "Active",
      minWidth: 90,
      align: "center",
      format: (value) => <StatusChip active={value === true} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 180,
      align: "right",
      format: (_value, row) => {
        const actions = [
          {
            icon: <LockResetIcon />,
            tooltip: "Change Password",
            onClick: () =>
              setPasswordDialog({ open: true, id: row.id, name: row.name }),
          },
          {
            icon: <EditIcon />,
            tooltip: "Edit",
            onClick: () => navigate(`/admin/users/${row.id}`),
          },
        ];

        if (!row.isSuperAdmin) {
          actions.unshift({
            icon: <ListAltIcon />,
            tooltip: "Assign Menus",
            onClick: () => navigate(`/admin/users/${row.id}/menus`),
          });
          actions.push({
            icon: <DeleteIcon />,
            isDelete: true,
            tooltip: "Delete",
            onClick: () =>
              setDeleteDialog({ open: true, id: row.id, title: row?.name }),
          });
        }

        return <ActionIcons actions={actions} />;
      },
    },
  ];

  return (
    <>
      <PageHeader
        title="Users"
        showSearch
        searchPlaceholder="Search users..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add User",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/users/create");
          },
        }}
      />
      <PageWrapper loading={loading}>
        <DataTable
          columns={columns}
          rows={users}
          totalCount={totalCount}
          onPaginationChange={handlePaginationChange}
          page={page}
          limit={limit}
          enableRowDrag={false}
        />
        <DeleteConfirmation
          open={deleteDialog.open}
          title={"Delete User - " + deleteDialog?.title}
          message="Are you sure you want to delete this user? This action cannot be undone."
          apiEndpoint={"users/"}
          deleteId={deleteDialog.id}
          onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
          refetchApi={() => {
            setPage(0);
            getUsers();
          }}
        />
        <ChangePasswordDialog
          open={passwordDialog.open}
          userId={passwordDialog.id}
          userName={passwordDialog.name}
          requireCurrentPassword={
            currentUser?.id === passwordDialog.id &&
            !currentUser?.isSuperAdmin
          }
          onClose={() =>
            setPasswordDialog({ open: false, id: 0, name: "" })
          }
        />
      </PageWrapper>
    </>
  );
}